import { LabValue } from '../types';

export interface ExtractionIssue {
  type: 'unit_error' | 'value_range_error' | 'missing_reference' | 'extraction_anomaly';
  severity: 'error' | 'warning';
  message: string;
  details?: string;
  suggestedFix?: {
    value?: number;
    units?: string;
  };
}

export interface ValidationResult {
  marker: string;
  hasIssues: boolean;
  issues: ExtractionIssue[];
  confidence: number; // 0-100
}

// Valid units by test category
const VALID_UNITS_BY_TEST: Record<string, { valid: string[], invalid: string[] }> = {
  // Lipid panel tests
  cholesterol: { 
    valid: ['mg/dl', 'mmol/l', 'mg/l'],
    invalid: ['u/l', 'u/i', 'iu/l', 'e9/l', '10^9/l', 'g/dl']
  },
  hdl: {
    valid: ['mg/dl', 'mmol/l'],
    invalid: ['u/l', 'u/i', 'iu/l', 'e9/l', '10^9/l', 'g/dl']
  },
  ldl: {
    valid: ['mg/dl', 'mmol/l'],
    invalid: ['u/l', 'u/i', 'iu/l', 'e9/l', '10^9/l', 'g/dl']
  },
  triglycerides: {
    valid: ['mg/dl', 'mmol/l'],
    invalid: ['u/l', 'u/i', 'iu/l', 'e9/l', '10^9/l']
  },
  
  // Enzyme tests
  alt: {
    valid: ['u/l', 'iu/l'],
    invalid: ['mg/dl', 'mmol/l', 'g/dl', 'e9/l']
  },
  ast: {
    valid: ['u/l', 'iu/l'],
    invalid: ['mg/dl', 'mmol/l', 'g/dl', 'e9/l']
  },
  ggt: {
    valid: ['u/l', 'iu/l'],
    invalid: ['mg/dl', 'mmol/l', 'g/dl', 'e9/l']
  },
  
  // Blood counts
  hemoglobin: {
    valid: ['g/dl', 'g/l', 'mmol/l'],
    invalid: ['u/l', 'e9/l', '10^9/l', 'mg/dl']
  },
  hematocrit: {
    valid: ['%', 'l/l'],
    invalid: ['g/dl', 'mg/dl', 'u/l']
  },
  
  // Cell counts
  'white blood cells': {
    valid: ['e9/l', '10^9/l', 'x10^9/l', 'k/ul', 'k/mcl'],
    invalid: ['mg/dl', 'g/dl', 'u/l']
  },
  wbc: {
    valid: ['e9/l', '10^9/l', 'x10^9/l', 'k/ul', 'k/mcl'],
    invalid: ['mg/dl', 'g/dl', 'u/l']
  },
  platelets: {
    valid: ['e9/l', '10^9/l', 'x10^9/l', 'k/ul', 'k/mcl'],
    invalid: ['mg/dl', 'g/dl', 'u/l']
  }
};

// Expected value ranges for common tests
const EXPECTED_RANGES: Record<string, { min: number, max: number, unit: string }> = {
  hdl_mg_dl: { min: 20, max: 100, unit: 'mg/dl' },
  hdl_mmol_l: { min: 0.5, max: 3.0, unit: 'mmol/l' },
  ldl_mg_dl: { min: 40, max: 200, unit: 'mg/dl' },
  ldl_mmol_l: { min: 1.0, max: 5.0, unit: 'mmol/l' },
  cholesterol_mg_dl: { min: 100, max: 300, unit: 'mg/dl' },
  cholesterol_mmol_l: { min: 2.5, max: 7.5, unit: 'mmol/l' },
  hemoglobin_g_dl: { min: 8, max: 20, unit: 'g/dl' },
  hemoglobin_g_l: { min: 80, max: 200, unit: 'g/l' },
  alt_u_l: { min: 0, max: 100, unit: 'u/l' },
  ast_u_l: { min: 0, max: 100, unit: 'u/l' },
  glucose_mg_dl: { min: 50, max: 400, unit: 'mg/dl' },
  glucose_mmol_l: { min: 2.8, max: 22.2, unit: 'mmol/l' }
};

export class ExtractionValidationService {
  static validateLabResults(results: Map<string, LabValue>): Map<string, ValidationResult> {
    const validationResults = new Map<string, ValidationResult>();
    
    results.forEach((labValue, marker) => {
      const issues: ExtractionIssue[] = [];
      let confidence = 100;
      
      // 1. Check unit validity
      const unitIssue = this.checkUnitValidity(marker, labValue);
      if (unitIssue) {
        issues.push(unitIssue);
        confidence -= unitIssue.severity === 'error' ? 50 : 25;
      }
      
      // 2. Check value range
      const rangeIssue = this.checkValueRange(marker, labValue);
      if (rangeIssue) {
        issues.push(rangeIssue);
        confidence -= rangeIssue.severity === 'error' ? 40 : 20;
      }
      
      // 3. Check for missing reference ranges (warning only)
      if (!labValue.reference_range || labValue.reference_range === 'N/A') {
        issues.push({
          type: 'missing_reference',
          severity: 'warning',
          message: 'No reference range provided',
          details: 'Unable to determine if value is normal without reference range'
        });
        confidence -= 10;
      }
      
      // 4. Check for extraction anomalies (e.g., suspiciously round numbers for certain tests)
      const anomalyIssue = this.checkExtractionAnomalies(marker, labValue);
      if (anomalyIssue) {
        issues.push(anomalyIssue);
        confidence -= 15;
      }
      
      validationResults.set(marker, {
        marker,
        hasIssues: issues.length > 0,
        issues,
        confidence: Math.max(0, confidence)
      });
    });
    
    return validationResults;
  }
  
  private static checkUnitValidity(marker: string, labValue: LabValue): ExtractionIssue | null {
    if (!labValue.units) return null;
    
    const markerLower = marker.toLowerCase();
    const unitLower = labValue.units.toLowerCase();
    
    // Find matching test configuration
    for (const [testKey, config] of Object.entries(VALID_UNITS_BY_TEST)) {
      if (markerLower.includes(testKey)) {
        // Check if unit is explicitly invalid
        const isInvalid = config.invalid.some(invalidUnit => 
          unitLower.includes(invalidUnit.toLowerCase())
        );
        
        if (isInvalid) {
          return {
            type: 'unit_error',
            severity: 'error',
            message: `Invalid unit "${labValue.units}" for ${marker}`,
            details: `${marker} should use units like ${config.valid.join(', ')}`,
            suggestedFix: {
              units: config.valid[0] // Suggest the most common unit
            }
          };
        }
        
        // Check if unit is in valid list
        const isValid = config.valid.some(validUnit => 
          unitLower.includes(validUnit.toLowerCase())
        );
        
        if (!isValid && config.valid.length > 0) {
          return {
            type: 'unit_error',
            severity: 'warning',
            message: `Unusual unit "${labValue.units}" for ${marker}`,
            details: `Expected units like ${config.valid.join(', ')}`
          };
        }
        
        break;
      }
    }
    
    return null;
  }
  
  private static checkValueRange(marker: string, labValue: LabValue): ExtractionIssue | null {
    if (!labValue.value || !labValue.units) return null;
    
    const markerLower = marker.toLowerCase();
    const unitLower = labValue.units.toLowerCase();
    
    // Build key for range lookup
    let rangeKey = '';
    if (markerLower.includes('hdl')) rangeKey = 'hdl_';
    else if (markerLower.includes('ldl')) rangeKey = 'ldl_';
    else if (markerLower.includes('cholesterol')) rangeKey = 'cholesterol_';
    else if (markerLower.includes('hemoglobin')) rangeKey = 'hemoglobin_';
    else if (markerLower.includes('alt')) rangeKey = 'alt_';
    else if (markerLower.includes('ast')) rangeKey = 'ast_';
    else if (markerLower.includes('glucose')) rangeKey = 'glucose_';
    else return null;
    
    // Add unit suffix
    if (unitLower.includes('mg/dl')) rangeKey += 'mg_dl';
    else if (unitLower.includes('mmol/l')) rangeKey += 'mmol_l';
    else if (unitLower.includes('g/dl')) rangeKey += 'g_dl';
    else if (unitLower.includes('g/l')) rangeKey += 'g_l';
    else if (unitLower.includes('u/l') || unitLower.includes('iu/l')) rangeKey += 'u_l';
    else return null;
    
    const expectedRange = EXPECTED_RANGES[rangeKey];
    if (!expectedRange) return null;
    
    if (labValue.value < expectedRange.min || labValue.value > expectedRange.max) {
      return {
        type: 'value_range_error',
        severity: 'error',
        message: `Value ${labValue.value} is outside expected range`,
        details: `${marker} typically ranges from ${expectedRange.min} to ${expectedRange.max} ${expectedRange.unit}`
      };
    }
    
    return null;
  }
  
  private static checkExtractionAnomalies(marker: string, labValue: LabValue): ExtractionIssue | null {
    const markerLower = marker.toLowerCase();
    
    // Check for HDL = 32 U/I specifically (the reported issue)
    if (markerLower.includes('hdl') && 
        labValue.value === 32 && 
        labValue.units?.toLowerCase().includes('u/')) {
      return {
        type: 'extraction_anomaly',
        severity: 'error',
        message: 'Likely extraction error detected',
        details: 'HDL value appears to be from an adjacent enzyme test. Common PDF extraction error.',
        suggestedFix: {
          units: 'mmol/l'
        }
      };
    }
    
    // Check for suspiciously round enzyme values with wrong units for lipids
    if ((markerLower.includes('cholesterol') || markerLower.includes('hdl') || markerLower.includes('ldl')) &&
        labValue.units?.toLowerCase().includes('u/') &&
        labValue.value && labValue.value % 5 === 0 && labValue.value >= 10 && labValue.value <= 100) {
      return {
        type: 'extraction_anomaly',
        severity: 'warning',
        message: 'Possible value/unit mismatch',
        details: 'Value and unit combination suggests possible extraction error from adjacent test'
      };
    }
    
    return null;
  }
  
  static getProblematicMarkers(validationResults: Map<string, ValidationResult>): string[] {
    const problematic: string[] = [];
    
    validationResults.forEach((result, marker) => {
      // Include markers with errors or confidence below 75%
      if (result.issues.some(issue => issue.severity === 'error') || result.confidence < 75) {
        problematic.push(marker);
      }
    });
    
    return problematic;
  }
}